import { apiErrorMessage } from '@agos/api-client'
import {
  T,
  TICKET_BODY_MAX,
  TICKET_CATEGORY_LABELS,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
  TICKET_STATUSES,
  formatDateTime,
  isTicketOutstanding,
  priorityNeedsSaying,
  ticketStatusLabel,
  ticketStatusTone,
  tt,
  type TicketPriority,
  type TicketStatus,
} from '@agos/shared'
import {
  BUTTON_PRIMARY,
  ButtonGroup,
  ConfirmDialog,
  EmptyState,
  IconButton,
  PageHeader,
  Spinner,
  StatusBadge,
  cn,
  useToast,
} from '@agos/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, LifeBuoy, Send, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  DEFAULT_QUEUE_FILTER,
  answerTicket,
  countByStatus,
  deleteTicketForGood,
  fetchQueue,
  fetchQueuedThread,
  matchesFilter,
  outstandingInQueue,
  platformMayMoveTo,
  removeTicket,
  triageTicket,
  type QueuedTicket,
} from './support-queue'

/**
 * Every franchise's tickets, in one queue - `SAAS.md` §3.137 and §3.140, owner O31.
 *
 * **Rebuilt as two panes, and the owner's own words are why**: *"I still hate these tickets... Redesign it
 * completely... Not sure what, but something is just wrong in the design."* It was a list of rows that
 * **expanded in place**, so a conversation - the whole point of a ticket - was crammed into a row's width,
 * the page reflowed under the cursor on every open, and the reply box sat at the bottom of an accordion.
 * **A conversation is not a list item.**
 *
 * Master-detail is what this application already does with a long list and a thing to read beside it -
 * `ModulesPage` is `grid lg:grid-cols-[1fr_20rem]` with a sticky `aside` - so this is the house pattern
 * rather than a new idea. The proportions are the other way round because here the **detail** is the
 * subject and the list is the index.
 *
 * **One read, filtered in the browser.** The endpoint still takes a status, and this asks for everything
 * instead: a support queue is dozens of rows, and having them all is what lets every filter carry a count
 * and switch without a round trip. A triager wants to know there are three urgent ones *before* clicking
 * the filter that shows them.
 *
 * **Every label and every allowed status comes from `@agos/shared`** (§9.510), held to `SupportTicketRules`
 * by a cross-stack guard, so nothing on this screen decides anything the server would disagree with.
 */
export function SupportTicketsPage() {
  /* §3.137's decision, which this rewrite reversed for one commit: the queue **opens on the new reports**,
     because *"a default view that includes closed tickets is a queue nobody trusts after a month"*. Its own
     test caught it - `agos-the-e2e-suite-records-decisions`, on a unit case. */
  const [filter, setFilter] = useState<TicketStatus | ''>(DEFAULT_QUEUE_FILTER)
  const [chosenId, setChosenId] = useState<number | null>(null)

  const queue = useQuery({ queryKey: ['tower-support-queue'], queryFn: () => fetchQueue('') })
  const all = useMemo(() => queue.data ?? [], [queue.data])
  const counts = useMemo(() => countByStatus(all), [all])
  const shown = useMemo(() => all.filter((one) => matchesFilter(one, filter)), [all, filter])
  const chosen = useMemo(
    () => (chosenId === null ? null : (all.find((one) => one.id === chosenId) ?? null)),
    [all, chosenId],
  )

  return (
    <main className="space-y-3" data-support-queue>
      <PageHeader
        title={tt('Tikety')}
        description={tt('Hlásenia od franšíz. Vyberte tiket a odpovedzte vpravo.')}
        icon={<LifeBuoy className="w-5 h-5" />}
      />

      {/* §3.140 - the product's own segmented chooser with a count per status, rather than eight hand-rolled
        * buttons that said nothing about what was behind them. §3.117 made the same argument for the other
        * Tower screens; this one was written after that pass and repeated the thing it removed. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ButtonGroup
          value={filter}
          onChange={(next) => setFilter(next as TicketStatus | '')}
          testIdKey="data-queue-filter"
          options={[
            { value: '', label: tt('Všetky'), count: all.length },
            ...TICKET_STATUSES.map((status) => ({
              value: status,
              label: ticketStatusLabel(status, 'platform'),
              count: counts[status] ?? 0,
            })),
          ]}
        />
        <p className="text-xs text-muted-foreground" data-queue-outstanding>
          {outstandingInQueue(all)} <T>otvorených</T>
        </p>
      </div>

      {queue.isLoading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}
      {queue.isError && (
        <p className="text-sm text-red-500" data-queue-error>
          {apiErrorMessage(queue.error, tt('Tikety sa nepodarilo načítať.'))}
        </p>
      )}

      {!queue.isLoading && !queue.isError && (
        <div className="grid gap-3 lg:grid-cols-[21rem_1fr] items-start">
          <div className="rounded-2xl border border-border overflow-hidden" data-queue-list>
            {shown.length === 0 ? (
              <EmptyState
                icon={<LifeBuoy className="w-6 h-6" />}
                title={tt('Žiadne tikety')}
                description={
                  all.length === 0
                    ? tt('Franšízy nič nenahlásili.')
                    : tt('V tomto stave nie sú žiadne hlásenia.')
                }
              />
            ) : (
              <ul className="max-h-[70dvh] overflow-y-auto divide-y divide-border">
                {shown.map((ticket) => (
                  <QueueRow
                    key={ticket.id}
                    ticket={ticket}
                    chosen={chosenId === ticket.id}
                    onChoose={() => setChosenId(ticket.id)}
                    onChanged={() => {
                      if (chosenId === ticket.id) setChosenId(null)
                      void queue.refetch()
                    }}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Sticky, so answering a long thread does not scroll the index away - `ModulesPage`'s own aside. */}
          <aside className="lg:sticky lg:top-4">
            {chosen === null ? (
              <p
                className="rounded-2xl border border-dashed border-border p-6 text-center text-xs text-muted-foreground"
                data-queue-nothing-chosen
              >
                <T>Vyberte tiket vľavo a zobrazí sa tu celá konverzácia.</T>
              </p>
            ) : (
              <Triage
                key={chosen.id}
                ticket={chosen}
                onChanged={() => void queue.refetch()}
                onGone={() => {
                  setChosenId(null)
                  void queue.refetch()
                }}
              />
            )}
          </aside>
        </div>
      )}
    </main>
  )
}

/**
 * One line of the index - §3.140.
 *
 * It carries what a triager sorts by and nothing else: whose it is, what it says, how old, and the two
 * badges. **No actions and no thread**, because both belong to the pane that has room for them - a row
 * with buttons on it is a row somebody clicks by accident while aiming at the row.
 */
function QueueRow({
  ticket,
  chosen,
  onChoose,
  onChanged,
}: {
  ticket: QueuedTicket
  chosen: boolean
  onChoose: () => void
  onChanged: () => void
}) {
  void onChanged
  return (
    <li>
      <button
        type="button"
        onClick={onChoose}
        data-queue-ticket={String(ticket.id)}
        data-queue-chosen={chosen ? 'yes' : 'no'}
        className={cn(
          'w-full text-left px-3 py-2.5 border-l-2 transition-colors',
          chosen ? 'bg-primary/5 border-l-primary' : 'hover:bg-muted/40',
          !chosen && isTicketOutstanding(ticket.status) ? 'border-l-amber-500' : '',
          !chosen && !isTicketOutstanding(ticket.status) ? 'border-l-transparent' : '',
        )}
      >
        <span className="flex items-center justify-between gap-2">
          {/* The franchise first and in weight: on a platform queue it is what a reader scans by, and it
            * used to be fourth in a middot-separated run of four facts. */}
          <span className="text-[11px] font-bold truncate" data-queue-franchise>
            {ticket.franchise_name}
          </span>
          <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
            {formatDateTime(ticket.created_at)}
          </span>
        </span>
        <span className="block text-sm font-medium truncate mt-0.5">{ticket.subject}</span>
        <span className="flex items-center gap-1.5 mt-1">
          <span data-queue-status={ticket.status}>
            <StatusBadge
              label={ticketStatusLabel(ticket.status, 'platform')}
              color={ticketStatusTone(ticket.status)}
            />
          </span>
          {/* Only when it deviates - four of five are `normal`, so a badge that always shows is width
            * spent saying nothing. §3.139. */}
          {priorityNeedsSaying(ticket.priority) && (
            <span data-queue-priority-badge={ticket.priority}>
              <StatusBadge
                label={TICKET_PRIORITY_LABELS[ticket.priority]}
                color={ticket.priority === 'low' ? 'zinc' : 'red'}
              />
            </span>
          )}
          <span className="text-[10px] text-muted-foreground truncate">
            {TICKET_CATEGORY_LABELS[ticket.category]}
          </span>
        </span>
      </button>
    </li>
  )
}

/**
 * The conversation, and everything that can be done to it - §3.140.
 *
 * <b>Keyed on the ticket id at the call site</b>, so choosing another one remounts this rather than leaving
 * a half-typed reply attached to somebody else's ticket. That is the bug an accordion could not have, and
 * the one a detail pane invites.
 */
function Triage({
  ticket,
  onChanged,
  onGone,
}: {
  ticket: QueuedTicket
  onChanged: () => void
  onGone: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [reply, setReply] = useState('')
  const [pending, setPending] = useState<'remove' | 'purge' | null>(null)

  const thread = useQuery({
    queryKey: ['tower-support-thread', ticket.id],
    queryFn: () => fetchQueuedThread(ticket.id),
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['tower-support-thread', ticket.id] })
    onChanged()
  }
  const failed = (caught: unknown, fallback: string) =>
    toast({
      variant: 'destructive',
      title: tt('Chyba'),
      description: apiErrorMessage(caught, fallback),
    })

  const answer = useMutation({
    mutationFn: () => answerTicket(ticket.id, reply),
    onSuccess: () => {
      setReply('')
      refresh()
    },
    onError: (caught: unknown) => failed(caught, tt('Odpoveď sa nepodarilo odoslať.')),
  })

  const triage = useMutation({
    mutationFn: (change: { status?: TicketStatus; priority?: TicketPriority }) =>
      triageTicket(ticket.id, change),
    onSuccess: refresh,
    onError: (caught: unknown) => failed(caught, tt('Zmenu sa nepodarilo uložiť.')),
  })

  const remove = useMutation({
    mutationFn: () => removeTicket(ticket.id),
    onSuccess: () => {
      toast({ title: tt('Tiket odstránený'), description: ticket.subject })
      onGone()
    },
    onError: (caught: unknown) => failed(caught, tt('Tiket sa nepodarilo odstrániť.')),
  })
  const purge = useMutation({
    mutationFn: () => deleteTicketForGood(ticket.id),
    onSuccess: () => {
      toast({ title: tt('Tiket zmazaný'), description: ticket.subject })
      onGone()
    },
    onError: (caught: unknown) => failed(caught, tt('Tiket sa nepodarilo zmazať.')),
  })

  return (
    <div className="rounded-2xl border border-border" data-queue-detail={String(ticket.id)}>
      <header className="px-3 py-2.5 border-b border-border flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold truncate">{ticket.subject}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {ticket.franchise_name} &middot; {TICKET_CATEGORY_LABELS[ticket.category]} &middot;{' '}
            {ticket.raised_by ?? tt('neuvedené')} &middot; {formatDateTime(ticket.created_at)}
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-1">
          {/* §10A.273 - two actions because they are two decisions: one tidies the queue and keeps the
            * thread, the other throws the conversation away. */}
          <IconButton
            label={`${tt('Odstrániť')} ${ticket.subject}`}
            onClick={() => setPending('remove')}
            attributes={{ 'data-queue-remove': String(ticket.id) }}
          >
            <Archive className="w-3.5 h-3.5" />
          </IconButton>
          <IconButton
            label={`${tt('Zmazať natrvalo')} ${ticket.subject}`}
            tone="red"
            onClick={() => setPending('purge')}
            attributes={{ 'data-queue-purge': String(ticket.id) }}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </IconButton>
        </div>
      </header>

      <div className="p-3 space-y-2 max-h-[46dvh] overflow-y-auto" data-queue-thread>
        {thread.isLoading && <Spinner className="w-4 h-4" />}
        {thread.isError && (
          <p className="text-xs text-red-500">
            {apiErrorMessage(thread.error, tt('Konverzáciu sa nepodarilo načítať.'))}
          </p>
        )}
        {(thread.data?.messages ?? []).map((message) => (
          <div
            key={message.id}
            data-queue-message={message.author_side}
            className={cn(
              'rounded-xl p-2.5 text-xs max-w-[85%]',
              message.author_side === 'platform'
                ? 'bg-primary/10 ml-auto'
                : 'bg-muted/60 mr-auto',
            )}
          >
            <p className="font-semibold text-[11px] text-muted-foreground">
              {message.author_name ?? message.author_side} &middot;{' '}
              {formatDateTime(message.created_at)}
            </p>
            <p className="whitespace-pre-wrap">{message.body}</p>
          </div>
        ))}
      </div>

      <div className="p-3 border-t border-border space-y-2">
        <textarea
          value={reply}
          maxLength={TICKET_BODY_MAX}
          onChange={(event) => setReply(event.target.value)}
          rows={3}
          placeholder={tt('Odpoveď franšíze')}
          aria-label={tt('Odpoveď')}
          data-queue-reply
          className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-queue-send
            disabled={reply.trim() === '' || answer.isPending}
            onClick={() => answer.mutate()}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs disabled:opacity-40',
              BUTTON_PRIMARY,
            )}
          >
            <Send className="w-3.5 h-3.5" />
            <T>Odpovedať</T>
          </button>

          {/* §10A.265 - anything but reopening a closed one, derived so a sixth status needs no edit here. */}
          {platformMayMoveTo(ticket.status).map((next) => (
            <button
              key={next}
              type="button"
              data-queue-move={next}
              disabled={triage.isPending}
              onClick={() => triage.mutate({ status: next })}
              className="px-3 py-2 rounded-xl border border-border text-xs font-semibold disabled:opacity-40"
            >
              {ticketStatusLabel(next, 'platform')}
            </button>
          ))}

          {/* A franchise's *urgent* is not the platform's, which is why this is re-rankable here. */}
          <select
            value={ticket.priority}
            aria-label={tt('Priorita')}
            data-queue-priority
            disabled={triage.isPending}
            onChange={(event) => triage.mutate({ priority: event.target.value as TicketPriority })}
            className="px-3 py-2 rounded-xl border border-border bg-background text-xs"
          >
            {TICKET_PRIORITIES.map((one) => (
              <option key={one} value={one}>
                {TICKET_PRIORITY_LABELS[one]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Both confirm, and the permanent one says out loud that it cannot be undone - the difference between
        * the two dialogs is the only thing standing between a tidy-up and a deleted conversation. */}
      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={() => {
          const what = pending
          setPending(null)
          if (what === 'remove') remove.mutate()
          if (what === 'purge') purge.mutate()
        }}
        variant="danger"
        title={pending === 'purge' ? tt('Zmazať tiket natrvalo?') : tt('Odstrániť tiket?')}
        description={
          pending === 'purge'
            ? `${ticket.subject} - ${tt('tiket aj celá konverzácia sa zmažú a nedajú sa vrátiť.')}`
            : `${ticket.subject} - ${tt('zmizne z oboch zoznamov, konverzácia sa zachová.')}`
        }
        confirmLabel={pending === 'purge' ? tt('Zmazať natrvalo') : tt('Odstrániť')}
      />
    </div>
  )
}
